import { FC, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import { OnboardingModulesTypes } from '@frontend/api-onboarding-modules';
import { GradientPage } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { ScheduleTrainingTaskCard, useScheduleTrainingTaskConfig } from '@frontend/schedule-training-task';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Text } from '@frontend/design-system';
import { IncompleteModuleTasksCompactCards } from '../../components/module-tasks-card-list';
import { ProgressBar } from '../../components/progress-bar';
import { TaskStatusCard } from '../../components/task-status-card';
import { ONBOARDING_DASHBOARD_PATH } from '../../constants';
import { useOnboardingDashboardConfig } from '../../hooks/use-onboarding-dashboard-config';
import { useOnboardingTasksSkip } from '../../hooks/use-onboarding-tasks-skip';
import { OnboardingModuleTasksContext } from '../../provider/onboarding-module-tasks.provider';
import { DashboardOnboardingProgressTrackingIds } from '../../tracking-ids';
import { getModuleTaskStats, getTaskById } from '../../utils';

interface OnboardingDashboardCompactProps {
  containerStyles?: Interpolation<Theme>;
}
export const OnboardingDashboardCompact: FC<OnboardingDashboardCompactProps> = ({ containerStyles }) => {
  const { t } = useTranslation('onboardingDashboard');
  const navigate = useNavigate();
  const onboardingDashboardConfig = useOnboardingDashboardConfig();
  const {
    selectedLocationId,
    showOnboardingDashboard,
    onboardingModuleTasks = [],
    enableSkipTasks,
    isReady,
    isLocationEligibleForV2Onboarding,
  } = onboardingDashboardConfig;

  const {
    showTrainingTask,
    hideTask: hideTrainingTask,
    isMutating: isHidingTrainingTask,
  } = useScheduleTrainingTaskConfig({
    locationId: selectedLocationId,
    checkEligibility: isLocationEligibleForV2Onboarding && isReady && !showOnboardingDashboard, // Only check eligibility if not showing the full dashboard
    checkTaskFromV2: true,
  });

  const { skipOnboardingTasks, isSkipInProgress } = useOnboardingTasksSkip({ locationId: selectedLocationId });

  const hasPendingScheduleTrainingTask = useMemo(() => {
    const task = getTaskById(OnboardingModulesTypes.TaskIds.Schedule1on1Training, onboardingModuleTasks);
    return task && task.status === OnboardingModulesTypes.TaskStatus.INCOMPLETE;
  }, [onboardingModuleTasks]);

  const { completed, progress, pending, total } = useMemo(() => {
    return getModuleTaskStats(onboardingModuleTasks);
  }, [onboardingModuleTasks]);

  const showSoloScheduleTrainingCard = showOnboardingDashboard
    ? pending === 1 && hasPendingScheduleTrainingTask
    : showTrainingTask;

  const handleSkipTasks = () => {
    skipOnboardingTasks();
    hasPendingScheduleTrainingTask && hideTrainingTask();
  };

  if (!(isLocationEligibleForV2Onboarding && isReady && (showOnboardingDashboard || showSoloScheduleTrainingCard))) {
    return null;
  }

  if (showSoloScheduleTrainingCard) {
    return (
      <ScheduleTrainingTaskCard.UIComponent
        locationId={selectedLocationId}
        isVisible
        onHideTask={hideTrainingTask}
        isMutating={isHidingTrainingTask}
        containerStyles={containerStyles}
      />
    );
  }

  return (
    <OnboardingModuleTasksContext.Provider value={onboardingDashboardConfig}>
      <div
        css={[styles.containerCard, containerStyles]}
        data-testid='onboarding-progress-compact'
        data-trackingid={DashboardOnboardingProgressTrackingIds.onboardingProgressCard}
      >
        <GradientPage gradientColor1={theme.colors.secondary.seaweed5} gradientColor2={theme.colors.indigo10}>
          <ContentLoader show={isSkipInProgress || isHidingTrainingTask} />
          <div css={styles.content}>
            <div css={styles.progressSection}>
              <TaskStatusCard.Body.MilestoneText color='subdued' completed={completed} total={total} />
              <div css={styles.progressTrackAndAction}>
                <ProgressBar
                  progress={progress}
                  containerStyles={{ flex: 1, minWidth: '40%' }}
                  barColor={theme.colors.indigo50}
                  trackColor={theme.colors.white}
                />
                <Button
                  variant='tertiary'
                  onClick={() => navigate({ to: ONBOARDING_DASHBOARD_PATH })}
                  trackingId={DashboardOnboardingProgressTrackingIds.viewAllTasks}
                  aria-label='View All Tasks'
                >
                  <Text as='span' weight='bold' size='medium' color='light'>
                    {t('View All')}
                  </Text>
                </Button>
              </div>
              {enableSkipTasks && (
                <Button
                  variant='tertiary'
                  onClick={handleSkipTasks}
                  disabled={isSkipInProgress}
                  aria-label='Skip Remaining Tasks'
                  trackingId={DashboardOnboardingProgressTrackingIds.skipRemaining}
                >
                  <Text as='span' weight='bold' size='medium' color='light'>
                    {t('Skip Remaining')}
                  </Text>
                </Button>
              )}
            </div>
            <TaskStatusCard.Header color={theme.colors.black} showLogo>
              {t('Welcome to Weave!')}
            </TaskStatusCard.Header>
            <IncompleteModuleTasksCompactCards moduleTasks={onboardingModuleTasks} />
          </div>
        </GradientPage>
      </div>
    </OnboardingModuleTasksContext.Provider>
  );
};

const styles = {
  containerCard: css`
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadius.large};
    padding: 0;
    box-shadow: ${theme.shadows.floating};
    position: relative;
    z-index: 0;
    overflow: hidden;
    container-type: inline-size;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};
  `,
  progressSection: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.min}px) {
      flex-direction: column;
      align-items: flex-end;
      gap: ${theme.spacing(1)};
    }
  `,
  progressTrackAndAction: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
    flex: 1;
    align-self: stretch;
  `,
};
